/**
 * Адопшн-инфраструктура: реестр «Что нового», прогресс пользователя в
 * localStorage, флаг подсказок.
 *
 * Правила пополнения WHATS_NEW: на каждый заметный релиз фич — новая запись
 * СВЕРХУ списка с монотонным id (дата). При загрузке страницы сравнивается
 * lastSeen: если появились новые записи и подсказки включены — авто-модалка
 * «Что нового» (один раз на версию). Каждая запись может нести tour-шаги
 * (ключи из tours.ts) — кнопка «Показать в интерфейсе».
 */

export interface WhatsNewEntry {
    /** Монотонный id (ISO-дата релиза; при двух релизах в день — суффикс). */
    id:    string;
    title: string;
    items: string[];
    /** Ключи целей тура (TOUR_TARGETS) — «Показать в интерфейсе». */
    tour?: { target: string; text: string }[];
}

export const WHATS_NEW: WhatsNewEntry[] = [
    {
        id:    '2026-07-24',
        title: 'Страница отвечает на вопрос «что мы решили и почему»',
        items: [
            'Блок «Какие развилки прошли» стал «Что решили и почему»: у каждого решения виден полный след — вопрос, что выбрали, что отвергли, почему и при каком условии вернёмся к вопросу.',
            'Решения сгруппированы по статусу (решено / пересмотр триггернут / отложено / ещё решаем) и выстроены в обратной хронологии — свежее сверху.',
            'Рамки (Предпосылки) сгруппированы по силе: твёрдые не обсуждаются, «на срок» живут до даты, мягкие — низкая уверенность.',
            'Содержание решений и рамок видно сразу в карточке, без «Показать детали»; на самих решениях появились значки Сигналов — на основании чего приняли.',
            'Тур по интерфейсу переписан: теперь это последовательность чтения (рамки → развилки → основания → открытые вопросы → как отразилось в продукте → как менялось), а не перечисление кнопок.',
        ],
        tour: [
            { target: 'aspect-frames',         text: 'Шаг 1: рамки — что было решено до рынка и не проверяется экспериментом.' },
            { target: 'aspect-decisions',      text: 'Шаг 2: пройденные развилки со всем следом — выбрали, отвергли, почему, когда вернёмся.' },
            { target: 'aspect-evidence',       text: 'Шаг 3: Сигналы — доказательная база под решениями.' },
            { target: 'aspect-open-questions', text: 'Шаг 4: что ещё не решено — рабочий список следующих развилок.' },
        ],
    },
    {
        id:    '2026-07-22',
        title: 'План, Сигналы, Вопросы, Отчёты — веб стал рабочей поверхностью команды',
        items: [
            'Страница «План» — дискавери-борд: гипотезы по горизонтам с порогами «да/нет», итерации с ёмкостью, Four Fits, Bullseye-кольца.',
            'Вкладка «Сигналы» в сайдбаре — лента «что нового узнали» с фильтром по категориям.',
            'Вкладка «Вопросы» — открытые GitHub issues проекта, «+ новая идея» в один клик.',
            'Страница «Отчёты» — журнал разборов концепта (reports/*.md) по ссылке.',
            'Карточка сущности: комментарии команды, кликабельные связи, история; открываются и dflow-сущности.',
            'На каждом блоке нарратива — «скопировать ссылку» и «зафиксировать идею как issue».',
            'Чат-агент умеет создавать GitHub issues и наводить подсветку на элементы интерфейса.',
            'Справочник: фильтр Наш/Рынок/Оба с раздельным счётчиком.',
        ],
        tour: [
            { target: 'nav-plan',       text: 'Дискавери-борд: гипотезы с порогами, итерации, Four Fits, Bullseye.' },
            { target: 'nav-reports',    text: 'Журнал разборов — отчёты и текстовый концепт по ссылке.' },
            { target: 'sidebar-tabs',   text: 'Новые вкладки: Сигналы (лента дискавери) и Вопросы (открытые issues).' },
            { target: 'aspect-actions', text: 'Ссылка на конкретный блок и фиксация идеи как issue — прямо с карточки.' },
            { target: 'chat-launcher',  text: 'Чат-агент: спросите «где посмотреть риски?» — он наведёт подсветку сам.' },
        ],
    },
];

const KEY_HINTS = 'pflow.adoption.hints';
const KEY_SEEN  = 'pflow.adoption.whatsNewSeen';
const KEY_TOURED = 'pflow.adoption.welcomeToured';

export function hintsEnabled(): boolean {
    return localStorage.getItem(KEY_HINTS) !== 'off';
}
export function setHintsEnabled(on: boolean): void {
    localStorage.setItem(KEY_HINTS, on ? 'on' : 'off');
}

export function latestWhatsNewId(): string {
    return WHATS_NEW[0]?.id ?? '';
}
export function lastSeenWhatsNew(): string {
    return localStorage.getItem(KEY_SEEN) ?? '';
}
export function markWhatsNewSeen(): void {
    localStorage.setItem(KEY_SEEN, latestWhatsNewId());
}
export function hasUnseenWhatsNew(): boolean {
    return latestWhatsNewId() > lastSeenWhatsNew();
}

export function welcomeToured(): boolean {
    return localStorage.getItem(KEY_TOURED) === 'yes';
}
export function markWelcomeToured(): void {
    localStorage.setItem(KEY_TOURED, 'yes');
}
